<template>
  <div class="lesson-comments">
    <h2 class="text-title-large font-medium mb-5">
      Comments ({{ comments.length }})
    </h2>

    <div class="lesson-comments-form-container">
      <div class="lesson-comments-form">
        <textarea
          v-model="newComment"
          placeholder="Share your experience with this lesson..."
          aria-label="Share your experience with this lesson"
          rows="4"
          class="text-title-big font-medium"
          :disabled="!isAuthenticated || isSubmitting"
        ></textarea>
        <div v-if="!isAuthenticated" class="auth-warning">
          You need to be logged in to comment.
        </div>
        <div class="form-footer">
          <ActionButton
            variant="primary"
            @click="addComment"
            class="send-button"
            :disabled="!isAuthenticated || isSubmitting || !newComment.trim()"
            :loading="isSubmitting"
          >
            Send
          </ActionButton>
        </div>
      </div>
    </div>

    <hr class="comments-separator" />

    <div v-if="isLoading" class="comments-loading">
      <div class="spinner"></div>
      <p>Loading comments...</p>
    </div>

    <div v-else-if="error" class="comments-error">
      <p>{{ error }}</p>
      <BaseButton variant="secondary" @click="fetchComments">
        Try Again
      </BaseButton>
    </div>

    <div v-else-if="comments.length === 0" class="no-comments">
      <p>No comments yet. Be the first to share your thoughts!</p>
    </div>

    <div v-else class="comments-list">
      <div v-for="comment in comments" :key="comment.id" class="comment-item">
        <div class="comment-main-content">
          <div class="comment-header">
            <div class="user-info">
              <div class="avatar">
                <ImageWrapper
                  :image-url="comment.user.image"
                  @error="handleImageError"
                />
              </div>
              <div class="user-metadata">
                <div class="username text-title-big font-regular">
                  {{ comment.user?.fullname || "Anonymous" }}
                </div>
                <div class="comment-date text-body-medium font-regular">
                  {{ formatDate(comment.created_at) }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="editingCommentId === comment.id" class="comment-edit-form">
            <textarea
              v-model="editedComment"
              rows="3"
              placeholder="Edit your comment..."
              aria-label="Edit your comment"
              ref="editTextarea"
              class="text-title-big font-medium"
            ></textarea>
            <div class="edit-actions">
              <ActionButton variant="secondary" @click="cancelEdit">
                Cancel
              </ActionButton>
              <ActionButton
                variant="primary"
                @click="updateComment(comment)"
                :disabled="isSubmitting || !editedComment.trim()"
                :loading="isSubmitting"
              >
                Save
              </ActionButton>
            </div>
          </div>

          <div v-else class="comment-content text-title-large font-medium">
            {{ comment.comment }}
          </div>

          <div class="comment-footer">
            <span class="like-count text-body-medium font-regular">
              Liked ({{ comment.likeCount || 0 }})
            </span>
          </div>
        </div>

        <div class="comment-actions-sidebar">
          <button
            class="like-button"
            :class="{ liked: comment.isLiked }"
            @click="toggleLike(comment)"
            :disabled="!isAuthenticated || isLikeLoading[comment.id]"
            :title="
              isAuthenticated
                ? comment.isLiked
                  ? 'Unlike'
                  : 'Like'
                : 'Sign In to like comments'
            "
            :aria-label="
              isAuthenticated
                ? comment.isLiked
                  ? 'Unlike'
                  : 'Like'
                : 'Sign In to like comments'
            "
          >
            <span v-if="isLikeLoading[comment.id]" class="like-loading"></span>
            <svg
              v-else
              class="like-icon"
              viewBox="0 0 24 24"
              :fill="comment.isLiked ? 'currentColor' : 'none'"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
              ></path>
            </svg>
          </button>

          <div v-if="isOwnComment(comment)" class="own-comment-actions">
            <button
              v-if="editingCommentId !== comment.id"
              class="action-btn edit-btn"
              @click="startEditing(comment)"
              title="Edit"
              aria-label="Edit comment"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                ></path>
                <path
                  d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                ></path>
              </svg>
            </button>
            <button
              class="action-btn delete-btn"
              @click="confirmDelete(comment)"
              title="Delete"
              aria-label="Delete comment"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path
                  d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                ></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <div v-if="showDeleteModal" class="delete-modal-overlay">
      <div class="delete-modal">
        <h3 class="text-title-big font-bold">Delete Comment</h3>
        <p class="text-body-medium">
          Are you sure you want to delete this comment? This action cannot be
          undone.
        </p>
        <div class="delete-modal-actions">
          <BaseButton variant="secondary" @click="cancelDelete"
            >Cancel</BaseButton
          >
          <BaseButton
            variant="danger"
            @click="deleteComment"
            :loading="isSubmitting"
          >
            Delete
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, defineProps } from "vue";
import { useStore } from "vuex";
import ImageWrapper from "./ImageWrapper.vue";
import ActionButton from "./ActionButton.vue";

const props = defineProps({
  lessonId: {
    type: [String, Number],
    required: true,
  },
  commentService: {
    type: Object,
    required: true,
    validator: (service) => {
      return (
        typeof service.getComments === "function" &&
        typeof service.createComment === "function" &&
        typeof service.updateComment === "function" &&
        typeof service.deleteComment === "function" &&
        typeof service.likeComment === "function"
      );
    },
  },
});

const store = useStore();
const userId = computed(() => store.state.user?.user_id);
const isAuthenticated = computed(() => !!userId.value);

// State variables
const comments = ref([]);
const newComment = ref("");
const editedComment = ref("");
const editingCommentId = ref(null);
const isLoading = ref(false);
const isSubmitting = ref(false);
const isLikeLoading = ref({});
const error = ref(null);
const showDeleteModal = ref(false);
const commentToDelete = ref(null);

// Fetch comments on component mount
onMounted(() => {
  fetchComments();
});

// Methods
const fetchComments = async () => {
  if (!props.lessonId) return;

  isLoading.value = true;
  error.value = null;
  try {
    const response = await props.commentService.getComments(props.lessonId);
    if (response.data && response.data.data && response.data.data.rows) {
      // Initialize like counts if not present
      comments.value = response.data.data.rows.map((comment) => ({
        ...comment,
        likeCount: comment.like_count || 0,
      }));
    } else {
      comments.value = [];
    }
  } catch (err) {
    console.error("Error fetching comments:", err);
    error.value = "Failed to load comments. Please try again later.";
  } finally {
    isLoading.value = false;
  }
};

const addComment = async () => {
  if (!isAuthenticated.value || !newComment.value.trim() || isSubmitting.value)
    return;

  isSubmitting.value = true;
  try {
    const payload = {
      comment: newComment.value.trim(),
      lessonId: props.lessonId,
    };

    const response = await props.commentService.createComment(payload);
    if (response.status === 201 || response.status === 200) {
      await fetchComments();
      newComment.value = "";
    }
  } catch (err) {
    console.error("Error adding comment:", err);
    alert("Failed to add comment. Please try again.");
  } finally {
    isSubmitting.value = false;
  }
};

const startEditing = (comment) => {
  editingCommentId.value = comment.id;
  editedComment.value = comment.comment;

  // Focus the edit textarea after DOM update
  nextTick(() => {
    if (document.querySelector("#edit-textarea")) {
      document.querySelector("#edit-textarea").focus();
    }
  });
};

const cancelEdit = () => {
  editingCommentId.value = null;
  editedComment.value = "";
};

const updateComment = async (comment) => {
  if (!editedComment.value.trim() || isSubmitting.value) return;

  isSubmitting.value = true;
  try {
    const payload = {
      comment: editedComment.value.trim(),
      lessonId: props.lessonId,
    };

    const response = await props.commentService.updateComment(
      comment.id,
      payload
    );
    if (response.status === 200) {
      // Update local comment data
      const index = comments.value.findIndex((c) => c.id === comment.id);
      if (index !== -1) {
        comments.value[index].comment = editedComment.value.trim();
      }
      cancelEdit();
    }
  } catch (err) {
    console.error("Error updating comment:", err);
    alert("Failed to update comment. Please try again.");
  } finally {
    isSubmitting.value = false;
  }
};

const confirmDelete = (comment) => {
  commentToDelete.value = comment;
  showDeleteModal.value = true;
};

const cancelDelete = () => {
  commentToDelete.value = null;
  showDeleteModal.value = false;
};

const deleteComment = async () => {
  if (!commentToDelete.value || isSubmitting.value) return;

  isSubmitting.value = true;
  try {
    const response = await props.commentService.deleteComment(
      commentToDelete.value.id
    );
    if (response.status === 200) {
      comments.value = comments.value.filter(
        (c) => c.id !== commentToDelete.value.id
      );
      cancelDelete();
    }
  } catch (err) {
    console.error("Error deleting comment:", err);
    alert("Failed to delete comment. Please try again.");
  } finally {
    isSubmitting.value = false;
  }
};

const toggleLike = async (comment) => {
  if (!isAuthenticated.value) return;

  // Set loading state for this specific comment
  isLikeLoading.value = { ...isLikeLoading.value, [comment.id]: true };

  try {
    const response = await props.commentService.likeComment(comment.id);
    if (response.status === 200) {
      // Toggle the liked status locally
      const index = comments.value.findIndex((c) => c.id === comment.id);
      if (index !== -1) {
        const newIsLiked = !comments.value[index].isLiked;
        comments.value[index].isLiked = newIsLiked;

        // Adjust like count based on the action
        comments.value[index].likeCount = newIsLiked
          ? (comments.value[index].likeCount || 0) + 1
          : Math.max(0, (comments.value[index].likeCount || 0) - 1);
      }
    }
  } catch (err) {
    console.error("Error toggling like:", err);
    // Optional: Show a toast or alert here
  } finally {
    // Clear loading state for this comment
    const updatedLoadingState = { ...isLikeLoading.value };
    delete updatedLoadingState[comment.id];
    isLikeLoading.value = updatedLoadingState;
  }
};

// Helper functions
const formatDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now - date;
  const diffInSecs = Math.floor(diffInMs / 1000);
  const diffInMins = Math.floor(diffInSecs / 60);
  const diffInHours = Math.floor(diffInMins / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInSecs < 60) return "Just now";
  if (diffInMins < 60)
    return `${diffInMins} minute${diffInMins !== 1 ? "s" : ""} ago`;
  if (diffInHours < 24)
    return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
  if (diffInDays < 7)
    return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const isOwnComment = (comment) => {
  return userId.value && comment.user_id === userId.value;
};

const handleImageError = () => {};
</script>

<style scoped>
.lesson-comments {
  width: 100%;
  margin: 0 auto;
  padding: 20px 0;
}

.lesson-comments h2 {
  color: var(--blue-dark-2);
}

.mb-5 {
  margin-bottom: 20px;
}

/* Form Styles */
.lesson-comments-form-container {
  width: 100%;
  margin-bottom: 32px;
}

.lesson-comments-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.lesson-comments-form textarea {
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid var(--neutral-700);
  background-color: var(--white);
  resize: vertical;
  min-height: 128px;
  color: var(--black-body-text);
  transition: border-color 0.2s;
}

.lesson-comments-form textarea:focus {
  outline: none;
  border-color: var(--primary-blue);
}

.form-footer {
  display: flex;
  justify-content: flex-end;
}

.send-button {
  min-width: 80px;
  height: 40px;
}

.comments-separator {
  border: none;
  border-top: 1px solid var(--border-color);
  margin: 32px 0;
}

/* Comments list styles */
.comments-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.comment-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}

.comment-main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  background-color: #e0e0e0;
  flex-shrink: 0;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-metadata {
  display: flex;
  flex-direction: column;
}

.username {
  color: var(--black-body-text);
}

.comment-date {
  color: var(--neutral-700);
}

.comment-content {
  color: var(--black-body-text);
  line-height: 28px;
  white-space: pre-line;
  word-break: break-word;
}

.comment-footer {
  margin-top: 4px;
}

.like-count {
  color: var(--primary-pink);
}

/* Sidebar Actions */
.comment-actions-sidebar {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-top: 15px;
}

.like-button {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--black-body-text);
  transition: all 0.2s ease;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.like-button:hover:not(:disabled) {
  opacity: 0.8;
}

.like-button.liked {
  color: var(--primary-pink);
}

.like-icon {
  width: 30px;
  height: 30px;
}

.own-comment-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--neutral-500);
  transition: color 0.2s;
}

.action-btn:hover {
  color: var(--primary-blue);
}

.delete-btn:hover {
  color: var(--error-red);
}

/* Edit form styles */
.comment-edit-form {
  margin: 10px 0;
}

.comment-edit-form textarea {
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--neutral-700);
  background-color: var(--white);
  resize: vertical;
  color: var(--black-body-text);
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}

/* Loading and error states */
.comments-loading,
.comments-error,
.no-comments {
  padding: 40px 20px;
  text-align: center;
  border-radius: 12px;
  background-color: #f8f9fa;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: var(--primary-blue);
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.auth-warning {
  color: var(--error-red);
  font-size: 14px;
}

/* Modal */
.delete-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.delete-modal {
  background-color: white;
  border-radius: 16px;
  padding: 24px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.delete-modal h3 {
  margin-bottom: 12px;
  color: var(--black-body-text);
}

.delete-modal p {
  margin-bottom: 24px;
  color: var(--neutral-700);
}

.delete-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

@media (max-width: 768px) {
  .comment-item {
    gap: 12px;
  }

  .avatar {
    width: 48px;
    height: 48px;
  }

  .like-icon {
    width: 18px;
    height: 18px;
  }
}
</style>
